<template>
  <div id="home" class="mx-auto max-w-screen-xl">
    <MoleculesLoadingOverlay v-if="loading" />
    <Organisms v-else :organisms="organisms" />
    <OrganismsModalForm>
      <header class="mb-4">
        <h2 class="mb-4 text-4xl font-semibold text-black font-archer">
          Be the First
        </h2>
        <p v-if="!isFormSubmitted" class="text-lg text-gray-500">
          Please provide us your information and we will get back to you.
        </p>
      </header>
      <FormsHubSpotForm
        :form="{ id: '767c334f-0893-4757-8fa6-fd4b253bd691' }"
        @submit="isFormSubmitted = true"
      />
    </OrganismsModalForm>
  </div>
</template>

<style>
#collections-intro-text {
  @apply max-w-4xl mx-auto mb-10 px-4;

  h2 {
    @apply font-semibold font-archer text-gray-900 leading-normal text-2xl lg:text-[33px];
  }
}

#lifefullness-logo-text {
  @apply max-w-4xl mx-auto mb-10 px-4;

  img {
    @apply mb-2 mx-auto;
  }

  h2 {
    @apply font-semibold font-archer text-bc-warm-gray leading-normal text-2xl lg:text-[33px];
  }

  .buttons {
    @apply justify-center mt-8;
  }
}

#collections-columns {
  @apply px-4 mb-28;

  .image-link {
    @apply hover:opacity-90;
  }

  img {
    @apply mb-4;
  }

  .title {
    @apply mb-4;

    &::before {
      @apply w-14 h-14 bg-no-repeat bg-contain relative top-4;
      content: "";
      display: none;
    }
  }

  #limited-edition-column .title::before,
  #wee-cottage-column .title::before,
  #affordable-wee-cottage-column .title::before,
  #easyhouse-column .title::before,
  #new-beginnings-column .title::before {
    display: inline-block;
  }

  .description p {
    @apply text-center text-lg font-arial;
    letter-spacing: 0.2px;
    line-height: 1.222em;
  }

  .buttons {
    @apply !justify-center;
  }

  #wee-cottage-column,
  #affordable-wee-cottage-column {
    .title {
      @apply text-bc-wc-green;

      &::before {
        background-image: url("/img/wee-cottage-icon.svg");
      }
    }
  }

  #easyhouse-column {
    .title {
      @apply text-bc-ezh-blue;

      &::before {
        background-image: url("/img/easyhouse-icon.svg");
      }
    }
  }

  #new-beginnings-column {
    .title {
      @apply text-bc-nb-blue;

      &::before {
        background-image: url("/img/new-beginnings-icon.svg");
      }
    }
  }

  #limited-edition-column {
    .title {
      @apply text-bc-le-gray;

      &::before {
        background-image: url("/img/limited_edition-icon.svg");
      }
    }
  }

  .buttons a {
    @apply uppercase;
  }
}

#testimonials-slider,
.testimonial-cta {
  @apply bg-gradient-to-r from-[#1685a7] to-[#0e6ea2];
}

#testimonials-slider::before {
  @apply block w-full h-10 bg-white;
  content: "";
}

.virtual-tour {
  .buttons a {
    @apply uppercase;
  }
}

#marshall-fire-rebuilding {
  @apply py-7;
}

.text-cta {
  p {
    @apply leading-8;

    a {
      @apply text-sm text-bc-dark-blue underline;
    }
  }

  .buttons a {
    @apply uppercase;
  }
}
</style>

<script setup>
import { useAsyncQuery } from "#imports";
import { GET_ORGANISMS } from "~/graphql/GetOrganisms";
import { storeToRefs } from "pinia";
const breadcrumbs = useBreadcrumbs();
const { breadcrumbsList } = storeToRefs(breadcrumbs);
const modalForm = useModalForm();
const isFormSubmitted = ref(false);

const { data, pending, error } = await useAsyncQuery(GET_ORGANISMS, {
  uri: "/",
});

const organisms = computed(() => {
  return data.value?.organisms?.hlOrganisms?.hlOrganism ?? [];
});

const heroVideoPoster = computed(() => {
  const findVideoNode = (node) => {
    if (!node || typeof node !== "object") return null;
    if (node.backgroundVideoFile) return node;
    if (Array.isArray(node.item)) {
      for (const child of node.item) {
        const hit = findVideoNode(child);
        if (hit) return hit;
      }
    }
    return null;
  };
  for (const o of organisms.value) {
    const hit = findVideoNode(o);
    if (hit) return hit.backgroundVideoPoster || "/img/hero-poster.jpg";
  }
  return null;
});

useHead(() => {
  const link = [];
  if (heroVideoPoster.value) {
    link.push({
      rel: "preload",
      as: "image",
      href: heroVideoPoster.value,
      fetchpriority: "high",
    });
  }
  return { link };
});

const loading = pending;

breadcrumbsList.value = [];

onMounted(() => {
  window.addEventListener(
    "hashchange",
    () => {
      if (window.location.hash === "#modal-form") {
        modalForm.openModal();
        window.location.hash = "";
      }
    },
    false,
  );

  addTriggers();
});

const addTriggers = () => {
  const modalFormTriggers = document.querySelectorAll(
    "#cta-be-the-first .button-1, #cta-be-the-first a[href='#modal-form']",
  );

  modalFormTriggers.forEach((node) => {
    // Clone & replace node to remove existing event listeners
    const clonedNode = node.cloneNode(true);
    node.parentNode.replaceChild(clonedNode, node);

    clonedNode.addEventListener("click", (event) => {
      // Add event listener
      event.preventDefault();
      modalForm.openModal();
    });
  });
};

onUpdated(() => {
  addTriggers();
});
</script>
